import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { authCheckUrl } from "config";
import type { Dispatch, Middleware, UnknownAction } from "@reduxjs/toolkit";
import Session from "users/__session/session";
import { HttpStatusCode } from "axios";
import { isFunction } from "lodash";

export type SignalThunk<S = unknown, D extends Dispatch = Dispatch<UnknownAction>, R = void | Promise<void>> = (
  dispatch: D,
  getState: () => S,
  invoke: HubConnection["invoke"],
) => R;

export type EventCallback<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> = (
  ...args: unknown[]
) => SignalThunk<S, D>;

export interface WithCallbacks<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  add(name: string, callback: EventCallback<S, D>): WithCallbacks<S, D>;
  callbackArray: Array<{ name: string; callback: EventCallback<S, D> }>;
}

export const withCallbacks = <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>(): WithCallbacks<S, D> => {
  const callbackArray = new Array<{ name: string; callback: EventCallback<S, D> }>();
  return {
    add(name: string, callback: EventCallback<S, D>) {
      callbackArray.push({ name, callback });
      return this;
    },
    callbackArray,
  };
};

export interface MiddlewareConfig<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  callbacks: WithCallbacks<S, D>;
  connection: HubConnection;
  onStart?: () => void;
  shouldConnectionStartImmediately?: boolean;
}

const isSignalThunk = (a: unknown): a is SignalThunk =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof a === "function" && (a as any).__signalThunk === true;

const startLocks = new WeakMap<HubConnection, Promise<void>>();
const lifecycleInstalled = new WeakSet<HubConnection>();

export const connect = (connection: HubConnection, onStart?: () => void): Promise<void> => {
  const existing = startLocks.get(connection);
  if (existing) return existing;

  const task = (async () => {
    const session = Session.loadFromStorage(() => {});
    const token = session.authToken?.value;

    if (session.authToken?.isExpired) session.logout();
    if (!token) return;

    if (connection.state !== HubConnectionState.Disconnected) return;

    try {
      await connection.start();
      onStart?.();
    } catch (err) {
      const authCheckResponse = await fetch(authCheckUrl);
      if (authCheckResponse.status === HttpStatusCode.Unauthorized) {
        console.warn("SignalR connection failed due to unauthorized session. Not retrying until re-login.");
        session.logout();
        return;
      }
      throw err;
    }
  })().finally(() => {
    startLocks.delete(connection);
  });

  startLocks.set(connection, task);
  return task;
};

const reduxSignalRMiddleware =
  <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>({
    callbacks,
    onStart,
    connection,
    shouldConnectionStartImmediately = true,
  }: MiddlewareConfig<S, D>): Middleware<object, S, D> =>
  (store) => {
    for (const { name, callback } of callbacks.callbackArray) {
      connection.on(name, (...args: unknown[]) => {
        try {
          const thunk = callback(...args);
          // For hub events, run the returned thunk regardless of tagging
          void Promise.resolve(
            thunk(store.dispatch, store.getState as () => S, connection.invoke.bind(connection)),
          ).catch((err) => {
            console.error(`SignalR handler for "${name}" failed:`, err);
          });
        } catch (err) {
          console.error(`SignalR callback for "${name}" threw:`, err);
        }
      });
    }

    ensureLifecycle(connection, onStart);

    if (shouldConnectionStartImmediately) {
      void connect(connection, onStart);
    }

    return (next) => (action) => {
      if (isSignalThunk(action)) {
        try {
          return action(store.dispatch, store.getState as () => S, connection.invoke.bind(connection));
        } catch (err) {
          console.error("SignalR thunk execution failed:", err);
          return undefined as unknown as D;
        }
      } else if (isFunction(action)) {
        try {
          action();
        } catch (err) {
          console.error("Non-signalR thunk execution failed:", err);
        }
      }
      return next(action as D);
    };
  };

function ensureLifecycle(connection: HubConnection, onStart?: () => void) {
  if (lifecycleInstalled.has(connection)) return;
  lifecycleInstalled.add(connection);

  connection.onreconnecting((error) => {
    console.info("SignalR connection dropped. Attempting to reconnect.", error);
  });

  connection.onclose((error) => {
    if (error) {
      console.info("SignalR connection closed after reconnect attempts were exhausted.", error);
    }
  });

  // Initial connection startup is still triggered by auth lifecycle events.
  document?.addEventListener("loggedIn", () => {
    void connect(connection, onStart);
  });

  // Stop when logging out
  document?.addEventListener("loggedOut", () => {
    if (connection.state !== HubConnectionState.Disconnected) {
      void connection.stop();
    }
  });
}

export default reduxSignalRMiddleware;
